import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { OdataService } from '@app/Services/o-data.service';
import { createChildGridOptions } from '@app/Shared/grid/data-explorer-grid-options';
import { iParsedEntity } from '@app/Shared/interfaces/iEntityParsed';
import { AppState } from '@app/Store';
import { dashboardFeatureSelectors } from '@app/Store/features/dashboard.feature';
import { select, Store } from '@ngrx/store';
import { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

interface SourcesColumnConfig {
    columnOrder: string[];
    hiddenFields: string[];
    columnCaptions: Record<string, string>;
}

@Component({
    selector: 'app-sources-tab',
    templateUrl: './sources-tab.component.html',
    styleUrls: ['./sources-tab.component.scss'],
})
export class SourcesTabComponent implements OnInit, OnDestroy {
    private readonly sourcesColumnConfig: SourcesColumnConfig = {
        columnOrder: [
            'Src_SrcNm',
            'SrcTrukey',
            '{Entity}SrcId',
            '{Entity}Trukey',
            '{Entity}SrcTrukey',
            'RcrdSrcStatNm',
            'RcrdStdStatNm',
            'RcrdStatPriortyLvlNbr',
            'RcrdCrtTs',
            'RcrdCrtUsrId',
            'RcrdLstModTs',
            'RcrdLstModUsrId',
        ],
        hiddenFields: ['Src_RcrdLstModTs', 'Src_SrcRcrdBusStatNm', 'Src_SrcTruid', 'Src_SrcTrukey', 'Src_RcrdCrtTs', 'Src_RcrdLstModUsrId', 'Src_RcrdCrtUsrId'],
        columnCaptions: {
            Src_SrcNm: 'Source',
            SrcTrukey: 'Source Trukey',
            '{Entity}SrcId': 'Source Identifier',
            '{Entity}Trukey': '{Entity}Trukey',
            '{Entity}SrcTrukey': '{Entity}SrcTrukey',
            RcrdSrcStatNm: 'Source Original Status',
            RcrdStdStatNm: 'Source Standardized Status',
            RcrdStatPriortyLvlNbr: 'Status Priority',
            RcrdCrtTs: 'Create Time',
            RcrdCrtUsrId: 'Create User',
            RcrdLstModTs: 'Modifiy Time',
            RcrdLstModUsrId: 'Modify User',
        },
    };

    public gridOptions: GridOptions;
    public columnDefs: ColDef[] = [];
    public sourcesRowData: any[] = [];
    public selectedEntity: iParsedEntity | null = null;
    public noRecordSelected = true;
    public loading = false;
    public errorMessage: string | null = null;

    private subscription = new Subscription();
    private gridApi: any;

    constructor(
        private store: Store<AppState>,
        private cdr: ChangeDetectorRef,
        private odataService: OdataService,
    ) {
        this.gridOptions = createChildGridOptions(() => false);
        this.gridOptions.suppressNoRowsOverlay = false;
        this.gridOptions.localeText = { noRowsToShow: 'No sources for this master record' };
    }

    ngOnInit(): void {
        this.subscription.add(
            combineLatest([
                this.store.pipe(
                    select(dashboardFeatureSelectors.selectSelectedEntity),
                    distinctUntilChanged((prev, curr) => prev?.entityName === curr?.entityName),
                ),
                this.store.pipe(
                    select(dashboardFeatureSelectors.selectSelectedRecords),
                    distinctUntilChanged((prev, curr) => {
                        if (prev?.length !== curr?.length) return false;
                        if (prev?.length === 0 && curr?.length === 0) return true;
                        return prev?.[0]?.data === curr?.[0]?.data;
                    }),
                ),
            ])
                .pipe(
                    distinctUntilChanged((prev, curr) => {
                        const [prevEntity, prevRecords] = prev;
                        const [currEntity, currRecords] = curr;
                        if (prevEntity?.entityName !== currEntity?.entityName) return false;
                        if (prevRecords?.length !== currRecords?.length) return false;
                        if (prevRecords?.length === 0 && currRecords?.length === 0) return true;
                        const prevKey = prevRecords?.[0]?.data?.[prevEntity?.key];
                        const currKey = currRecords?.[0]?.data?.[currEntity?.key];
                        return prevKey === currKey;
                    }),
                )
                .subscribe(([entity, selectedRecords]) => {
                    if (entity && selectedRecords?.length > 0) {
                        this.selectedEntity = entity;
                        this.noRecordSelected = false;
                        const masterKey = selectedRecords[0].data[entity.key];
                        this.fetchSources(entity, masterKey);
                    } else {
                        this.noRecordSelected = true;
                        this.selectedEntity = null;
                        this.sourcesRowData = [];
                        this.columnDefs = [];
                        this.errorMessage = null;
                        this.updateGridData();
                        this.cdr.detectChanges();
                    }
                }),
        );
    }
    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private fetchSources(entity: iParsedEntity, masterKey: number | string): void {
        this.loading = true;
        this.errorMessage = null;
        this.cdr.detectChanges();

        this.odataService.getEntitySources(entity.entityName, entity.key, masterKey).subscribe({
            next: (response) => {
                this.loading = false;
                const value = response?.value;
                if (!value || value.length === 0) {
                    this.sourcesRowData = [];
                    this.columnDefs = [];
                } else {
                    const sourcesCollectionKey = entity.entityName.charAt(0).toUpperCase() + entity.entityName.slice(1) + 'Srcs';
                    const rawSrcs = value[0][sourcesCollectionKey];
                    const clientSrcs: any[] = Array.isArray(rawSrcs) ? rawSrcs : rawSrcs && typeof rawSrcs === 'object' && Array.isArray(rawSrcs.value) ? rawSrcs.value : [];
                    if (clientSrcs.length === 0) {
                        this.sourcesRowData = [];
                        this.columnDefs = [];
                    } else {
                        const flattened = this.flattenSources(clientSrcs, entity);
                        this.sourcesRowData = flattened.rows;
                        this.columnDefs = flattened.columnDefs;
                    }
                }
                this.updateGridData();
                this.cdr.detectChanges();
            },
            error: (err) => {
                this.loading = false;
                this.errorMessage = err?.message || 'Failed to load sources';
                this.sourcesRowData = [];
                this.columnDefs = [];
                this.updateGridData();
                this.cdr.detectChanges();
            },
        });
    }

    /**
     * Flatten ClientSrcs items: each item has Src nested; flatten to one row per item with Src fields prefixed.
     */
    private flattenSources(clientSrcs: any[], entity: iParsedEntity): { rows: any[]; columnDefs: ColDef[] } {
        const rows = clientSrcs.map((item) => {
            const flat: any = {};
            for (const [k, v] of Object.entries(item)) {
                if (k.startsWith('@')) continue; // Skip OData metadata
                if (v != null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)) {
                    Object.entries(v as Record<string, unknown>).forEach(([nk, nv]) => {
                        if (nk.startsWith('@')) return; // Skip OData metadata in nested objects
                        flat[k + '_' + nk] = nv;
                    });
                } else {
                    flat[k] = v;
                }
            }
            return flat;
        });

        const allKeys = new Set<string>();
        rows.forEach((r) => Object.keys(r).forEach((k) => allKeys.add(k)));

        const findMatchingKey = (pattern: string, keys: Set<string>): string | null => {
            if (!pattern.includes('{Entity}')) {
                return keys.has(pattern) ? pattern : null;
            }
            const suffix = pattern.replace(/\{Entity\}/g, '');
            const longerSuffixes = this.sourcesColumnConfig.columnOrder
                .filter((p) => p.includes('{Entity}'))
                .map((p) => p.replace(/\{Entity\}/g, ''))
                .filter((s) => s.length > suffix.length && s.endsWith(suffix));
            for (const key of keys) {
                if (!key.endsWith(suffix)) continue;
                if (key === suffix) continue; // Prefer entity-prefixed key: {Entity}SrcTrukey → ClientSrcTrukey, not SrcTrukey
                if (longerSuffixes.some((ls) => key.endsWith(ls))) continue;
                return key;
            }
            return null;
        };

        const orderPatterns = this.sourcesColumnConfig.columnOrder;
        const orderedKeys: string[] = [];
        const used = new Set<string>();
        for (const pattern of orderPatterns) {
            const key = findMatchingKey(pattern, allKeys);
            if (key && !used.has(key)) {
                orderedKeys.push(key);
                used.add(key);
            }
        }
        const hiddenPatterns = this.sourcesColumnConfig.hiddenFields;
        const isHidden = (key: string) => hiddenPatterns.some((p) => findMatchingKey(p, new Set([key])) === key);
        const visibleKeys = Array.from(allKeys)
            .filter((k) => !isHidden(k))
            .sort((a, b) => {
                const ai = orderedKeys.indexOf(a);
                const bi = orderedKeys.indexOf(b);
                if (ai !== -1 && bi !== -1) return ai - bi;
                if (ai !== -1) return -1;
                if (bi !== -1) return 1;
                return a.localeCompare(b);
            });

        const entityDisplayName = entity?.entityName ? entity.entityName.charAt(0).toUpperCase() + entity.entityName.slice(1) : '';
        const captionsByKey: Record<string, string> = {};
        Object.entries(this.sourcesColumnConfig.columnCaptions).forEach(([pattern, caption]) => {
            const key = pattern.includes('{Entity}') ? findMatchingKey(pattern, allKeys) : pattern;
            if (key) {
                const resolvedCaption = (caption || '').replace(/\{Entity\}/gi, entityDisplayName);
                captionsByKey[key] = resolvedCaption;
            }
        });

        const defaultHeader = (field: string) =>
            field
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, (s) => s.toUpperCase())
                .trim();

        const columnDefs: ColDef[] = visibleKeys.map((field) => ({
            field,
            headerName: captionsByKey[field] ?? defaultHeader(field),
            sortable: true,
            resizable: true,
            flex: 1,
            minWidth: 250,
        }));

        return { rows, columnDefs };
    }

    private updateGridData(): void {
        if (this.gridApi) {
            this.gridApi.setColumnDefs(this.columnDefs);
            this.gridApi.setRowData(this.sourcesRowData || []);
        }
    }

    onGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;
        this.gridApi.setColumnDefs(this.columnDefs);
        this.gridApi.setRowData(this.sourcesRowData || []);
    }
}
